import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import type { Client, ClientFormData } from '@/types/api'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const clientFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  display_order: z.number().min(0).default(0),
  is_active: z.boolean().default(true),
})

interface ClientFormProps {
  className?: string
  initialData?: Client
  onSubmit: (_data: ClientFormData) => void
  onCancel: () => void
  isLoading?: boolean
}

export const ClientForm: React.FC<ClientFormProps> = ({
  className,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: initialData?.name || '',
      display_order: initialData?.display_order || 0,
      is_active: initialData?.is_active ?? true,
    },
  })

  const [logoFile, setLogoFile] = React.useState<File | null>(null)

  const onFormSubmit = (data: z.infer<typeof clientFormSchema>) => {
    onSubmit({
      name: data.name,
      display_order: data.display_order,
      is_active: data.is_active,
      logo: logoFile || undefined,
    })
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{initialData ? 'Edit Client' : 'Add Client'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <FormField id="name" label="Client Name" error={!!errors.name} required>
            <Input {...register('name')} placeholder="Client Name" />
          </FormField>

          <FormField id="logo" label="Logo" required={!initialData}>
            <div className="space-y-2">
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setLogoFile(e.target.files[0])
                  }
                }}
              />
              {initialData?.logo_path && !logoFile && (
                <div className="text-sm text-gray-500">
                  Current logo: <span className="font-medium">{initialData.logo_path}</span>
                </div>
              )}
            </div>
          </FormField>

          <FormField id="display_order" label="Display Order">
            <Input type="number" {...register('display_order', { valueAsNumber: true })} />
          </FormField>

          <FormField id="is_active" label="Status">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active_check"
                {...register('is_active')}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="is_active_check" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>
          </FormField>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
